/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState, useEffect, useRef } from "react";
import useForm from "react-hook-form";
import {
  getRoleOptions,
  authCheckCallApi,
  nameValidator,
} from "../../Authenticator/utils";
import { countryOptions } from "../../Authenticator/countries";
import {
  Form,
  Button,
  Dropdown,
  DropdownProps,
  Checkbox,
  Dimmer,
  Loader,
  Grid,
  CheckboxProps,
  Accordion,
} from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import { useLazyQuery, useMutation } from "@apollo/react-hooks";
import {
  UserQuery,
  UserData,
  DisableUserMutation,
  EnableUserMutation,
  UpdateUserPermissionMutation,
} from "../../../graphql/user";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import { ChangeEmailDialog } from ".";
import ChangeImage from "./ChangeImage";
import { Ico } from "../../Icon/Icon";
import userImage from "../../Icon/User.svg";
import cStyles from "./../../Components.module.css";
import "../MyAccount.css";
import ConfirmationDialog from "../../ConfirmationDialog";
import ValidationError from "../../Authenticator/ValidationError";
import { useTranslation } from "react-i18next";

interface ContactFormProps {
  onFormSubmit: any;
  onCancel?: any;
  userId: string;
  currentUserRole: string;
  setShowSetPassword?: any;
  setContactFormLoading?: any;
  readOnly: boolean;
  isMonitoringEnabled?: boolean;
}

const ContactForm: React.FC<ContactFormProps> = ({
  onFormSubmit,
  onCancel,
  userId,
  currentUserRole,
  setShowSetPassword,
  setContactFormLoading,
  readOnly,
  isMonitoringEnabled,
}) => {
  const isPopup = !!onCancel;
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const { t } = useTranslation("common");

  const [
    getUser,
    { loading: getUserLoading, data: userData, error: queryError },
  ] = useLazyQuery<UserData>(UserQuery, {
    variables: { userId },
    fetchPolicy: "no-cache",
    onError: () => {
      setContactFormLoading && setContactFormLoading(false);
      if (!message) {
        setMessage({
          title: t("load_user"),
          message: t("load_user_error"),
        });
      }
    },
    onCompleted: () => {
      setContactFormLoading && setContactFormLoading(false);
      if (!message && queryError) {
        setMessage({
          title: t("load_user"),
          message: t("fetch_user_error"),
        });
      }
    },
  });

  const [showChangeEmail, setShowChangeEmail] = useState(false);

  const [disableUser, { loading: disableUserLoading }] = useMutation(
    DisableUserMutation,
    {
      onError: () => {
        setMessage({
          title: t("disable_user"),
          message: t("disable_user_error"),
        });
      },
    }
  );

  const [enableUser, { loading: enableUserLoading }] = useMutation(
    EnableUserMutation,
    {
      onError: () => {
        setMessage({
          title: t("enable_user"),
          message: t("enable_user_error"),
        });
      },
    }
  );

  const [updateUserPermission, { loading: updateUserPermissionLoading }] =
    useMutation(UpdateUserPermissionMutation, {
      onError: () => {
        setMessage({
          title: t("update_permission"),
          message: t("update_permission_error"),
        });
      },
      onCompleted: () => {
        authCheckCallApi(getUser);
      },
    });

  const [showContactInfo, setShowContactInfo] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  useEffect(() => {
    setContactFormLoading && setContactFormLoading(true);
    authCheckCallApi(getUser);
  }, [getUser, setContactFormLoading]);

  type FormData = {
    id: string;
    name: string;
    email: string;
    jobTitle: string;
    role: string;
    addressLine1: string;
    addressLine2: string;
    addressCity: string;
    addressCountry: string;
    addressZip: string;
    addressState: string;
    addressTelephone1: string;
    addressTelephone2: string;
    userEnabled: any;
    [key: string]: any;
  };

  const emptyInputs = {
    id: "",
    name: "",
    email: "",
    jobTitle: "",
    role: "",
    addressLine1: "",
    addressLine2: "",
    addressCity: "",
    addressCountry: "",
    addressZip: "",
    addressState: "",
    addressTelephone1: "",
    addressTelephone2: "",
    userEnabled: false,
  };

  const { register, handleSubmit, errors, setValue } = useForm<FormData>();
  const currentUserUpdated = useRef<number>();

  useEffect(() => {
    register({ name: "addressCountry" }, { required: true });
    register({ name: "role" }, { required: true });
    register({ name: "userEnabled" }, { required: false });
    register({ name: "id" }, { required: true });
  }, [register]);

  const [inputs, setInputs] = useState<FormData>(emptyInputs);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const handleDropdownChange = (event: any, data: DropdownProps) => {
    setValue(data.name, data.value);
    setInputs((inputs) => ({ ...inputs, [data.name]: data.value }));
  };

  const setUserEnabled = (userEnabled: any) => {
    if (!userData || !Array.isArray(userData.user.organizations)) {
      return;
    }
    const userId = userData.user.id;
    const orgId = userData.user.organizations[0].id;

    if (userEnabled) {
      authCheckCallApi(enableUser, {
        variables: {
          userId: userId,
          orgId: orgId,
        },
      });
    } else {
      authCheckCallApi(disableUser, {
        variables: {
          userId: userId,
          orgId: orgId,
        },
      });
    }
  };

  const handleDisableUserChanged = (data: CheckboxProps) => {
    const { name, checked } = data;
    if (name && name === "userEnabled") {
      setInputs((inputs) => ({ ...inputs, [name]: checked }));
      setUserEnabled(checked);
    }
  };

  if (
    userData &&
    userData.user &&
    currentUserUpdated.current !== userData.user.updated
  ) {
    const user = userData.user;
    currentUserUpdated.current = user.updated;
    inputs.name = user.name;
    inputs.email = user.email;
    if (Array.isArray(user.organizations)) {
      inputs.jobTitle = user.organizations[0].jobTitle;
      inputs.role = user.organizations[0].role;
      setValue("role", user.organizations[0].role);
      inputs.userEnabled = !user.organizations[0].disabled;
      setValue("userEnabled", !user.organizations[0].disabled);
    }
    if (user.address) {
      inputs.addressLine1 = user.address.line1;
      inputs.addressLine2 = user.address.line2 || "";
      inputs.addressCity = user.address.city;
      inputs.addressCountry = user.address.country.toLocaleLowerCase();
      inputs.addressZip = user.address.zip;
      inputs.addressState = user.address.state || "";
      inputs.addressTelephone1 = user.address.telephone1 || "";
      inputs.addressTelephone2 = user.address.telephone2 || "";
      setValue("addressCountry", user.address.country.toLocaleLowerCase());
    }
    setValue("id", user.id);
  }

  const dimmerActive =
    (!setContactFormLoading && getUserLoading) ||
    disableUserLoading ||
    enableUserLoading ||
    updateUserPermissionLoading;

  const contactInfoExpanded = showContactInfo || Object.keys(errors).length > 0;

  const accordionStyle = { paddingTop: "10px", paddingBottom: "10px" };

  const hasPermission = (permission: string) =>
    userData?.user.organizations?.[0]?.permissions?.includes(permission);

  const isOrgAdmin = userData?.user.organizations?.[0]?.role === "ADMIN";

  return (
    <React.Fragment>
      <Dimmer active={dimmerActive}>
        <Loader size="large" active={dimmerActive}>
          {(getUserLoading && "Loading...") ||
            ((disableUserLoading || enableUserLoading) && "Saving...")}
        </Loader>
      </Dimmer>
      <Form onSubmit={handleSubmit(onFormSubmit)}>
        <input type="submit" hidden />
        <Grid>
          <Grid.Row style={{ paddingBottom: 0 }}>
            <Grid.Column computer={isPopup ? 16 : 12} mobile={16} tablet={16}>
              {!isPopup && (
                <div className={styles.sectionHeaderMargin}>
                  {t("personal_details")}
                </div>
              )}

              <Form.Field required error={!!errors.name}>
                <Form.Group inline>
                  <label className={styles.myAccountLabel}>
                    {t("full_name")}
                  </label>
                  <input
                    name="name"
                    key="name"
                    className={styles.myAccountInput}
                    placeholder={t("full_name")}
                    ref={register({
                      required: t("name_required") as string,
                      pattern: {
                        value: nameValidator,
                        message: t("valid_name"),
                      },
                    })}
                    defaultValue={inputs.name}
                    onChange={handleInputChange}
                    disabled={readOnly || !inputs.userEnabled}
                  />
                </Form.Group>
              </Form.Field>

              {!!errors.name && (
                <Form.Field required error={!!errors.name}>
                  <Form.Group inline>
                    <label className={styles.myAccountLabel}></label>
                    <ValidationError
                      errors={errors}
                      name="name"
                      pointing="above"
                      internal={true}
                    />
                  </Form.Group>
                </Form.Field>
              )}

              <Form.Group inline>
                <label className={styles.myAccountLabel}>{t("email")}</label>
                {(isPopup || readOnly) && (
                  <p
                    className={
                      readOnly || !inputs.userEnabled ? styles.formLabelRo : ""
                    }
                  >
                    {inputs.email}
                  </p>
                )}
                {!isPopup && !readOnly && (
                  <span
                    className={styles.myAccountLink}
                    onClick={() => setShowChangeEmail(true)}
                  >
                    <Ico
                      name="pencil"
                      style={{
                        marginRight: "5px",
                        verticalAlign: "text-bottom",
                      }}
                      className={styles.ico}
                    />
                    {inputs.email}
                  </span>
                )}
              </Form.Group>

              <Form.Field inline>
                <Form.Group inline>
                  <label className={styles.myAccountLabel}>
                    {t("job_title")}
                  </label>
                  <input
                    className={styles.myAccountInput}
                    placeholder={t("job_title")}
                    name="jobTitle"
                    key="jobTitle"
                    ref={register({ required: false })}
                    defaultValue={inputs.jobTitle}
                    autoComplete="off"
                    onChange={handleInputChange}
                    disabled={readOnly || !inputs.userEnabled}
                  />
                </Form.Group>
              </Form.Field>

              <Form.Field inline>
                <Form.Group inline>
                  <label className={styles.myAccountLabel}>{t("role")}</label>
                  <Dropdown
                    className={styles.myAccountInput}
                    name="role"
                    key="role"
                    fluid
                    selection
                    options={getRoleOptions(currentUserRole, true)}
                    placeholder={t("select_role")}
                    value={inputs.role}
                    onChange={handleDropdownChange}
                    disabled={readOnly || !isPopup || !inputs.userEnabled}
                    icon={<Ico name="down" className={styles.dropdownIcon} />}
                  />
                </Form.Group>
              </Form.Field>

              {isPopup && (
                <Form.Field>
                  <Form.Group inline>
                    <label className={styles.myAccountLabel}>
                      {t("profile_image")}
                    </label>
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {(getUserLoading && (
                        <Loader inline active size="tiny" />
                      )) || (
                        <img
                          src={userData?.user.imageUrl || userImage}
                          alt="Profile"
                          className={styles.profileImage}
                        />
                      )}
                    </div>
                  </Form.Group>
                </Form.Field>
              )}

              {!isPopup && !readOnly && (
                <Form.Group inline>
                  <label className={styles.myAccountLabel}>
                    {t("password")}
                  </label>
                  <span
                    className={styles.myAccountLink}
                    onClick={() => setShowSetPassword(true)}
                  >
                    <Ico
                      name="pencil"
                      style={{
                        marginRight: "5px",
                        verticalAlign: "text-bottom",
                      }}
                      className={styles.ico}
                    />
                    {t("change_password")}
                  </span>
                </Form.Group>
              )}
            </Grid.Column>

            {!isPopup && userData && (
              <Grid.Column computer={4} mobile={16} tablet={16}>
                <ChangeImage
                  id={userData.user.id}
                  readOnly={readOnly}
                  imageUrl={userData.user.imageUrl}
                  onImageChanged={() => {
                    authCheckCallApi(getUser);
                  }}
                />
              </Grid.Column>
            )}
          </Grid.Row>

          <Grid.Row style={{ paddingBottom: 0, paddingTop: 0 }}>
            <Grid.Column computer={isPopup ? 16 : 12} mobile={16} tablet={16}>
              <Accordion style={accordionStyle}>
                <Accordion.Title
                  className={styles.advancedTitle}
                  active={contactInfoExpanded}
                  index={1}
                  onClick={() => {
                    if (Object.keys(errors).length > 0) {
                      return;
                    }
                    setShowContactInfo(!showContactInfo);
                  }}
                >
                  <Ico
                    name={showContactInfo ? "down" : "right"}
                    className={styles.accordionIcon}
                  />
                  {t("contact_information")}
                </Accordion.Title>
                <Accordion.Content active={contactInfoExpanded}>
                  <Form.Field inline required error={!!errors.addressLine1}>
                    <Form.Group inline>
                      <label className={styles.myAccountLabel}>
                        {t("address")}
                      </label>
                      <input
                        className={styles.myAccountInput}
                        placeholder={t("street_address")}
                        name="addressLine1"
                        key="addressLine1"
                        ref={register({
                          required: t("street_address_required") as string,
                        })}
                        defaultValue={inputs.addressLine1}
                        onChange={handleInputChange}
                        disabled={readOnly || !inputs.userEnabled}
                      />
                    </Form.Group>
                  </Form.Field>

                  <Form.Field inline>
                    <Form.Group inline>
                      <label className={styles.myAccountLabel}>
                        {t("address_2")}
                      </label>
                      <input
                        className={styles.myAccountInput}
                        name="addressLine2"
                        key="addressLine2"
                        ref={register({ required: false })}
                        placeholder={t("address_2")}
                        defaultValue={inputs.addressLine2}
                        onChange={handleInputChange}
                        disabled={readOnly || !inputs.userEnabled}
                      />
                    </Form.Group>
                  </Form.Field>

                  <Form.Field inline required error={!!errors.addressCity}>
                    <Form.Group inline>
                      <label className={styles.myAccountLabel}>
                        {t("city_town")}
                      </label>
                      <input
                        className={styles.myAccountInput}
                        placeholder={t("city_town")}
                        name="addressCity"
                        key="addressCity"
                        ref={register({ required: "City is required" })}
                        defaultValue={inputs.addressCity}
                        onChange={handleInputChange}
                        disabled={readOnly || !inputs.userEnabled}
                      />
                    </Form.Group>
                  </Form.Field>

                  <Form.Field inline required error={!!errors.addressZip}>
                    <Form.Group inline>
                      <label className={styles.myAccountLabel}>
                        {t("zip_code")}
                      </label>
                      <input
                        className={styles.myAccountInput}
                        placeholder={t("zip_post")}
                        name="addressZip"
                        key="addressZip"
                        ref={register({
                          required: t("zip_required") as string,
                        })}
                        defaultValue={inputs.addressZip}
                        onChange={handleInputChange}
                        disabled={readOnly || !inputs.userEnabled}
                      />
                    </Form.Group>
                  </Form.Field>

                  <Form.Field inline>
                    <Form.Group inline>
                      <label className={styles.myAccountLabel}>
                        {t("state")}
                      </label>
                      <input
                        className={styles.myAccountInput}
                        placeholder={t("state_country")}
                        name="addressState"
                        key="addressState"
                        ref={register({ required: false })}
                        defaultValue={inputs.addressState}
                        onChange={handleInputChange}
                        disabled={readOnly || !inputs.userEnabled}
                      />
                    </Form.Group>
                  </Form.Field>

                  <Form.Field inline required error={!!errors.addressCountry}>
                    <Form.Group inline>
                      <label className={styles.myAccountLabel}>
                        {t("country")}
                      </label>
                      <Dropdown
                        className={styles.myAccountInput}
                        name="addressCountry"
                        key="addressCountry"
                        fluid
                        search
                        selection
                        options={countryOptions}
                        placeholder={t("select_country")}
                        value={inputs.addressCountry}
                        onChange={handleDropdownChange}
                        disabled={readOnly || !inputs.userEnabled}
                        icon={
                          <Ico name="down" className={styles.dropdownIcon} />
                        }
                      />
                    </Form.Group>
                  </Form.Field>

                  <Form.Field inline>
                    <Form.Group inline>
                      <label className={styles.myAccountLabel}>
                        {t("telephone_1")}
                      </label>
                      <input
                        className={styles.myAccountInput}
                        placeholder={t("telephone_1")}
                        name="addressTelephone1"
                        key="addressTelephone1"
                        ref={register({ required: false })}
                        defaultValue={inputs.addressTelephone1}
                        onChange={handleInputChange}
                        disabled={readOnly || !inputs.userEnabled}
                      />
                    </Form.Group>
                  </Form.Field>

                  <Form.Field inline>
                    <Form.Group inline>
                      <label className={styles.myAccountLabel}>
                        {t("telephone_2")}
                      </label>
                      <input
                        className={styles.myAccountInput}
                        placeholder={t("telephone_2")}
                        name="addressTelephone2"
                        key="addressTelephone2"
                        ref={register({ required: false })}
                        defaultValue={inputs.addressTelephone2}
                        onChange={handleInputChange}
                        disabled={readOnly || !inputs.userEnabled}
                      />
                    </Form.Group>
                  </Form.Field>
                </Accordion.Content>
              </Accordion>

              {isPopup && (
                <React.Fragment>
                  {isMonitoringEnabled && (
                    <Accordion style={accordionStyle}>
                      <Accordion.Title
                        className={styles.advancedTitle}
                        active={showPermissions}
                        index={1}
                        onClick={() => {
                          setShowPermissions(!showPermissions);
                        }}
                      >
                        <Ico
                          name={showPermissions ? "down" : "right"}
                          className={styles.accordionIcon}
                        />
                        {t("permissions")}
                      </Accordion.Title>
                      <Accordion.Content active={showPermissions}>
                        <Form.Group
                          inline
                          style={{ justifyContent: "space-between" }}
                        >
                          <label className={styles.myAccountLabel}>
                            {t("monitor")}
                          </label>
                          <Checkbox
                            disabled={
                              readOnly || !inputs.userEnabled || isOrgAdmin
                            }
                            toggle
                            name="monitorEnabled"
                            key="monitorEnabled"
                            checked={
                              isOrgAdmin ||
                              hasPermission("xtd:monitoring")
                            }
                            onChange={(_, data) => {
                              if (
                                !userData ||
                                !Array.isArray(userData.user.organizations)
                              ) {
                                return;
                              }

                              authCheckCallApi(updateUserPermission, {
                                variables: {
                                  userId: userData.user.id,
                                  permission: "xtd:monitoring",
                                  add: data.checked,
                                },
                              });
                            }}
                          />
                        </Form.Group>

                        <Form.Group
                          inline
                          style={{ justifyContent: "space-between" }}
                        >
                          <label className={styles.myAccountLabel}>
                            {t("aiid_mgmt")}
                          </label>
                          <Checkbox
                            disabled={
                              readOnly || !inputs.userEnabled || isOrgAdmin
                            }
                            toggle
                            name="appInstanceMgmtEnabled"
                            key="appInstanceMgmtEnabled"
                            checked={
                              isOrgAdmin ||
                              hasPermission(
                                "xtd:app_instance_mgmt"
                              )
                            }
                            onChange={(_, data) => {
                              if (
                                !userData ||
                                !Array.isArray(userData.user.organizations)
                              ) {
                                return;
                              }

                              authCheckCallApi(updateUserPermission, {
                                variables: {
                                  userId: userData.user.id,
                                  permission:
                                    "xtd:app_instance_mgmt",
                                  add: data.checked,
                                },
                              });
                            }}
                          />
                        </Form.Group>
                      </Accordion.Content>
                    </Accordion>
                  )}

                  <Accordion style={accordionStyle}>
                    <Accordion.Title
                      className={styles.advancedTitle}
                      active={showAdvanced}
                      index={1}
                      onClick={() => {
                        setShowAdvanced(!showAdvanced);
                      }}
                    >
                      <Ico
                        name={showAdvanced ? "down" : "right"}
                        className={styles.accordionIcon}
                      />
                      {t("advanced")}
                    </Accordion.Title>
                    <Accordion.Content active={showAdvanced}>
                      <Form.Group
                        inline
                        style={{ justifyContent: "space-between" }}
                      >
                        <label className={styles.myAccountLabel}>
                          {t("access")}
                        </label>
                        <Checkbox
                          disabled={readOnly}
                          toggle
                          label={
                            <label>
                              {t("user_is")}{" "}
                              {inputs.userEnabled
                                ? t("enabled")
                                : t("disabled")}
                            </label>
                          }
                          name="userEnabled"
                          key="userEnabled"
                          checked={inputs["userEnabled"]}
                          onChange={(_, data) => handleDisableUserChanged(data)}
                        />
                      </Form.Group>
                    </Accordion.Content>
                  </Accordion>
                </React.Fragment>
              )}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row style={{ paddingBottom: 0 }}>
            <Grid.Column stretched>
              <div className={styles.buttonContainer}>
                {inputs.userEnabled && !readOnly && (
                  <Button type="submit" primary className={cStyles.buttonPrime}>
                    <Ico name="check" style={{ marginRight: "10px" }} />
                    {isPopup ? t("ok") : t("save")}
                  </Button>
                )}

                {isPopup && (
                  <Button
                    style={{ marginRight: "1rem" }}
                    secondary
                    className={cStyles.buttonSecond}
                    onClick={() => onCancel(null)}
                  >
                    <Ico name="cancel" style={{ marginRight: "10px" }} />
                    {t("close")}
                  </Button>
                )}
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
      {message && (
        <ConfirmationDialog
          title={message.title}
          message={message.message}
          onOk={() => setMessage(null)}
        />
      )}
      {showChangeEmail && (
        <ChangeEmailDialog
          currentEmail={inputs.email}
          userId={userId}
          onChangeEmailComplete={() => setShowChangeEmail(false)}
        />
      )}
    </React.Fragment>
  );
};

export default ContactForm;
